import { TooltipDirective } from 'src/app/directives/tooltip.directive';
import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import {
  faArrowLeft,
  faArrowDown,
  faArrowRight,
  faArrowUp,
  faCirclePlus,
  faEdit,
  faMinus,
  faTrash,
  faUsers
} from '@fortawesome/free-solid-svg-icons';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { EnumAcl } from '@simplyfly/shared';
import { EnumEnquiryType } from '@simplyfly/shared';
import { EnumLanguage } from '@simplyfly/shared';
import { IClientPassenger } from '@simplyfly/shared';
import { IEnquiryFlight } from '@simplyfly/shared';
import { IEnquiry } from '@simplyfly/shared';
import { IFlightBriefCatering } from '@simplyfly/shared';
import {
  EnumFlightBriefPassengerCivility,
  IFlightBriefPassengerInfo,
  IFlightBriefPetInfo,
  EnumFlightBriefPetType
} from '@simplyfly/shared';
import { getFlightBriefPassengerCivilityLabel } from 'src/app/interfaces/flight-brief.helpers';
import { generateRandomId } from 'src/app/misc.utils';
import { AclService } from 'src/app/services/acl.service';
import { ClientPassengerService } from 'src/app/services/client-passengers/client-passengers.service';
import { FlightBriefCateringService } from 'src/app/services/flight-brief-caterings/flight-brief-caterings.service';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';

import { FlightBriefCateringEditComponent } from 'src/app/admin/flight-brief-caterings/flight-brief-catering-edit/flight-brief-catering-edit.component';

export const comparePassengersAlphabetically = (
  a: Pick<IFlightBriefPassengerInfo, 'id' | 'civility' | 'firstname' | 'lastname'>,
  b: Pick<IFlightBriefPassengerInfo, 'id' | 'civility' | 'firstname' | 'lastname'>
): number => {
  for (const field of ['lastname', 'firstname', 'civility', 'id']) {
    const valueA: string = (a[field] || '').toString().toLocaleLowerCase();
    const valueB: string = (b[field] || '').toString().toLocaleLowerCase();

    if (valueA < valueB) {
      return -1;
    }

    if (valueA > valueB) {
      return 1;
    }
  }

  return 0;
};

export const getOrderedPassengers = <T extends IFlightBriefPassengerInfo>(
  passengers: T[],
  disablePassengerSorting?: boolean
): T[] => {
  if (disablePassengerSorting) {
    return [...passengers];
  }

  return [...passengers].sort(comparePassengersAlphabetically);
};

export const getOrderedPassengerIds = (
  passengerIds: string[],
  passengersById: Record<string, IClientPassenger>,
  disablePassengerSorting?: boolean
): string[] => {
  if (disablePassengerSorting) {
    return [...passengerIds];
  }

  return passengerIds
    .map((passengerId: string) => passengersById[passengerId])
    .filter((passenger: IClientPassenger | undefined): passenger is IClientPassenger => !!passenger)
    .sort(comparePassengersAlphabetically)
    .map((passenger: IClientPassenger) => passenger.id);
};

export const getPassengerSnapshot = (
  passenger: Pick<IClientPassenger, 'id' | 'civility' | 'firstname' | 'lastname'>
): IFlightBriefPassengerInfo => ({
  id: passenger.id,
  civility: passenger.civility,
  firstname: passenger.firstname,
  lastname: passenger.lastname
});

export const getOrderedPassengerSnapshots = (
  passengerIds: string[],
  passengersById: Record<string, IClientPassenger>,
  disablePassengerSorting?: boolean
): IFlightBriefPassengerInfo[] => {
  const orderedPassengerIds: string[] = getOrderedPassengerIds(
    passengerIds,
    passengersById,
    disablePassengerSorting
  );

  return orderedPassengerIds
    .map((passengerId: string) => passengersById[passengerId])
    .filter((passenger: IClientPassenger | undefined): passenger is IClientPassenger => !!passenger)
    .map(getPassengerSnapshot);
};

@Component({
    selector: 'app-enquiry-flight-edit-passengers',
    templateUrl: './enquiry-flight-edit-passengers.component.html',
    styleUrls: ['./enquiry-flight-edit-passengers.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
      TooltipDirective,
      FormsModule,
      ReactiveFormsModule,
      FaIconComponent,
      FlightBriefCateringEditComponent,
      TranslateModule
    ]
})
export class EnquiryFlightEditPassengersComponent implements OnInit, OnChanges, OnDestroy {
  @Input() enquiry: IEnquiry | null = null;
  @Input() enquiryFlight: IEnquiryFlight | null = null;

  @Output() goPrev: EventEmitter<void> = new EventEmitter();
  @Output() saveAndGoNext: EventEmitter<IEnquiryFlight> = new EventEmitter();

  @ViewChild('modalFlightBriefCatering', { static: false })
  modalFlightBriefCateringElement: ElementRef;
  @ViewChild('modalClientPassengerEdit', { static: false })
  modalClientPassengerEditElement: ElementRef;
  @ViewChild('modalClientPassengerView', { static: false })
  modalClientPassengerViewElement: ElementRef;

  form: FormGroup = this.resetForm();

  faArrowLeft = faArrowLeft;
  faArrowRight = faArrowRight;
  faArrowUp = faArrowUp;
  faArrowDown = faArrowDown;
  faUsers = faUsers;
  faTrash = faTrash;
  faMinus = faMinus;
  faCirclePlus = faCirclePlus;
  faEdit = faEdit;

  EnumEnquiryType = EnumEnquiryType;
  EnumLanguage = EnumLanguage;
  EnumAcl = EnumAcl;
  EnumFlightBriefPetType = EnumFlightBriefPetType;

  getFlightBriefPassengerCivilityLabel = getFlightBriefPassengerCivilityLabel;

  petTypeOptions: Array<{ value: EnumFlightBriefPetType; labelKey: string }> = [
    {
      value: EnumFlightBriefPetType.dog,
      labelKey: 'ENQUIRY_FLIGHT_EDIT.PASSENGERS.PET_PRESET.DOG'
    },
    {
      value: EnumFlightBriefPetType.cat,
      labelKey: 'ENQUIRY_FLIGHT_EDIT.PASSENGERS.PET_PRESET.CAT'
    },
    {
      value: EnumFlightBriefPetType.other,
      labelKey: 'ENQUIRY_FLIGHT_EDIT.PASSENGERS.PET_PRESET.OTHER'
    }
  ];

  flightBriefCaterings: IFlightBriefCatering[] = [];
  displayedFlightBriefCaterings: IFlightBriefCatering[] = [];
  loadingFlightBriefCaterings = false;
  loadingClientPassengers = false;
  clientPassengers: IClientPassenger[] = [];
  selectedPassengerToAddId: string | null = null;
  readonly createNewPassengerOptionValue: string = '__create_new__';
  editingClientPassenger: IClientPassenger | null = null;
  viewingClientPassenger: IClientPassenger | null = null;
  savingEditedClientPassenger = false;
  editClientPassengerForm: FormGroup;
  translationObj: { [key: string]: any } = {};

  editingFlightBriefCatering: IFlightBriefCatering | null = null;

  subscriptionsForCatering = new Subscription();
  subscriptionsForClientPassengers = new Subscription();
  subscriptionsTranslations = new Subscription();
  private petTypeSubscriptions: Subscription[] = [];

  constructor(
    private formBuilder: FormBuilder,
    private flightBriefCateringService: FlightBriefCateringService,
    private translateService: TranslateService,
    private aclService: AclService,
    private clientPassengerService: ClientPassengerService
  ) {
    this.editClientPassengerForm = this.formBuilder.group({
      civility: [EnumFlightBriefPassengerCivility.mr, [Validators.required]],
      firstname: ['', [Validators.required]],
      lastname: ['', [Validators.required]]
    });
  }

  ngOnInit(): void {
    this.applyEnquiryFlight();

    this.loadClientPassengers();
    this.loadFlightBriefCaterings();
    this.loadTranslation();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes.enquiryFlight &&
      (!changes.enquiryFlight.previousValue ||
        changes.enquiryFlight.previousValue !== changes.enquiryFlight.currentValue)
    ) {
      this.applyEnquiryFlight();
      this.loadClientPassengers();
    }
  }

  ngOnDestroy(): void {
    this.subscriptionsForCatering.unsubscribe();
    this.subscriptionsForClientPassengers.unsubscribe();
    this.subscriptionsTranslations.unsubscribe();
    this.disposePetTypeSubscriptions();
  }

  get pax(): FormControl {
    return this.form.get('pax') as FormControl;
  }

  get boardingMinutesBeforeDeparture(): FormControl {
    return this.form.get('boardingMinutesBeforeDeparture') as FormControl;
  }

  get passengersId(): FormControl {
    return this.form.get('passengersId') as FormControl;
  }

  get disablePassengerSorting(): FormControl {
    return this.form.get('disablePassengerSorting') as FormControl;
  }

  get isManualPassengerSortingEnabled(): boolean {
    return !!this.disablePassengerSorting.value;
  }

  get pets(): FormArray {
    return this.form.get('pets') as FormArray;
  }

  getPet(i: number): FormGroup {
    return this.pets.at(i) as FormGroup;
  }

  getPetField(i: number, field: string): FormControl {
    return this.getPet(i).get(field) as FormControl;
  }

  get flightBriefCateringId(): FormControl {
    return this.form.get('flightBriefCateringId') as FormControl;
  }

  get catering(): FormControl {
    return this.form.get('catering') as FormControl;
  }

  get beverages(): FormControl {
    return this.form.get('beverages') as FormControl;
  }

  get luggageQty(): FormControl {
    return this.form.get('luggageQty') as FormControl;
  }

  get luggagesWeight(): FormControl {
    return this.form.get('luggagesWeight') as FormControl;
  }

  get luggageIsCustom(): FormControl {
    return this.form.get('luggageIsCustom') as FormControl;
  }

  get luggages(): FormControl {
    return this.form.get('luggages') as FormControl;
  }

  get luggagesLimitInHoldInKg(): FormControl {
    return this.form.get('luggagesLimitInHoldInKg') as FormControl;
  }

  get luggagesLimitInCabinInKg(): FormControl {
    return this.form.get('luggagesLimitInCabinInKg') as FormControl;
  }

  get selectedClientPassengers(): IClientPassenger[] {
    if (!Array.isArray(this.passengersId?.value)) {
      return [];
    }

    const selectedIds: string[] = this.getOrderedPassengerIdsForCurrentMode();
    const clientPassengersById: Record<string, IClientPassenger> = this.getClientPassengersById();

    const orderedSelected: IClientPassenger[] = [];
    for (const id of selectedIds) {
      if (clientPassengersById[id]) {
        orderedSelected.push(clientPassengersById[id]);
      }
    }

    return orderedSelected;
  }

  get availableClientPassengersToAdd(): IClientPassenger[] {
    const selectedIds: string[] = Array.isArray(this.passengersId?.value)
      ? this.passengersId.value
      : [];

    return getOrderedPassengers(
      this.clientPassengers.filter((item: IClientPassenger) => !selectedIds.includes(item.id))
    );
  }

  getCivilities(): EnumFlightBriefPassengerCivility[] {
    return Object.values(EnumFlightBriefPassengerCivility);
  }

  applyEnquiryFlight(): void {
    this.form = this.resetForm();

    if (this.enquiryFlight) {
      for (const field in this.form.value) {
        if (typeof this.enquiryFlight[field] !== 'undefined') {
          switch (field) {
            default:
              this.form.get(field).setValue(this.enquiryFlight[field]);
              break;
            case 'passengers':
              break;
            case 'pets':
              this.disposePetTypeSubscriptions();
              this.pets.clear();

              for (const pet of this.enquiryFlight.pets) {
                this.addPetAndSet(pet);
              }

              break;
            case 'flightBriefCateringId':
              this.enquiryFlight = this.refreshFlightBriefCateringId(this.enquiryFlight);

              this.form.get(field).setValue(this.enquiryFlight[field]);
              break;
          }
        }
      }

      if (this.enquiryFlight.passengersId?.length) {
        this.passengersId.setValue([...this.enquiryFlight.passengersId]);
      } else if (this.enquiryFlight.passengers?.length) {
        const legacyPassengerIds: string[] = this.enquiryFlight.passengers
          .map((passenger: IFlightBriefPassengerInfo) => passenger?.id)
          .filter((id: string) => !!id);
        this.passengersId.setValue(legacyPassengerIds);
      } else {
        this.passengersId.setValue([]);
      }

      this.refreshDisplayedFlightBriefCaterings();

      this.form.enable();
    }
  }

  refreshFlightBriefCateringId(enquiryFlight: IEnquiryFlight): IEnquiryFlight {
    // Fix for previous system

    if (this.flightBriefCaterings.length) {
      for (const flightBriefCatering of this.flightBriefCaterings) {
        if (flightBriefCatering.id !== enquiryFlight.flightBriefCateringId) {
          for (const language in flightBriefCatering.textByLanguage) {
            if (
              flightBriefCatering.textByLanguage[language] === enquiryFlight.flightBriefCateringId
            ) {
              enquiryFlight.flightBriefCateringId = flightBriefCatering.id;
              break;
            }
          }
        }
      }
    }

    return enquiryFlight;
  }

  private disposePetTypeSubscriptions(): void {
    if (!this.petTypeSubscriptions || !this.petTypeSubscriptions.length) {
      this.petTypeSubscriptions = [];
      return;
    }

    for (const sub of this.petTypeSubscriptions) {
      sub?.unsubscribe();
    }

    this.petTypeSubscriptions = [];
  }

  private createPetFormGroup(pet?: IFlightBriefPetInfo): FormGroup {
    const initialPetType: EnumFlightBriefPetType = this.resolveInitialPetType(pet);

    const group = new FormGroup({
      id: new FormControl(pet?.id || generateRandomId(), [Validators.required]),
      petType: new FormControl(initialPetType, Validators.required),
      type: new FormControl(
        initialPetType === EnumFlightBriefPetType.other ? pet?.type || null : null
      ),
      name: new FormControl(pet?.name || null, [Validators.required])
    });

    this.applyPetTypeValidation(group, initialPetType);

    const petTypeControl = group.get('petType') as FormControl;
    const subscription = petTypeControl.valueChanges.subscribe((value: EnumFlightBriefPetType) => {
      this.applyPetTypeValidation(group, value);
    });

    this.petTypeSubscriptions.push(subscription);

    return group;
  }

  private resolveInitialPetType(pet?: IFlightBriefPetInfo): EnumFlightBriefPetType {
    if (!pet) {
      return EnumFlightBriefPetType.dog;
    }

    return pet.petType || EnumFlightBriefPetType.other;
  }

  private applyPetTypeValidation(group: FormGroup, petType: EnumFlightBriefPetType): void {
    const typeControl = group.get('type') as FormControl;

    if (petType === EnumFlightBriefPetType.other) {
      typeControl.setValidators([Validators.required]);
    } else {
      typeControl.clearValidators();

      if (typeControl.value !== null) {
        typeControl.setValue(null, { emitEvent: false });
      }
    }

    typeControl.updateValueAndValidity({ emitEvent: false });
  }

  resetForm(): FormGroup {
    this.disposePetTypeSubscriptions();

    return new FormGroup({
      pax: new FormControl(null),
      boardingMinutesBeforeDeparture: new FormControl(20),
      passengersId: new FormControl([]),
      disablePassengerSorting: new FormControl(false),
      pets: new FormArray([]),
      flightBriefCateringId: new FormControl(null),
      catering: new FormControl(null),
      luggageQty: new FormControl(1),
      luggagesWeight: new FormControl(15),
      luggageIsCustom: new FormControl(false),
      luggagesLimitInHoldInKg: new FormControl(15),
      luggagesLimitInCabinInKg: new FormControl(5),
      luggages: new FormControl(null),
      beverages: new FormControl(null)
    });
  }

  togglePassenger(passengerId: string): void {
    const currentIds: string[] = Array.isArray(this.passengersId.value)
      ? [...this.passengersId.value]
      : [];
    const index: number = currentIds.indexOf(passengerId);

    if (index >= 0) {
      currentIds.splice(index, 1);
    } else {
      currentIds.push(passengerId);
    }

    this.updateSelectedPassengerIds(currentIds);
  }

  private updateSelectedPassengerIds(passengerIds: string[], synchronizePax: boolean = true): void {
    this.passengersId.setValue(passengerIds);

    if (synchronizePax) {
      this.pax.setValue(passengerIds.length);
    }
  }

  getClientPassengersById(): Record<string, IClientPassenger> {
    const clientPassengersById: Record<string, IClientPassenger> = {};

    for (const passenger of this.enquiryFlight?.passengers || []) {
      clientPassengersById[passenger.id] = {
        ...passenger,
        pipedriveOrganisationId: this.enquiryFlight?.clientId || null
      } as IClientPassenger;
    }

    for (const passenger of this.clientPassengers || []) {
      clientPassengersById[passenger.id] = passenger;
    }

    return clientPassengersById;
  }

  getOrderedPassengerIdsForCurrentMode(): string[] {
    const selectedIds: string[] = Array.isArray(this.passengersId.value)
      ? [...this.passengersId.value]
      : [];

    return getOrderedPassengerIds(
      selectedIds,
      this.getClientPassengersById(),
      this.isManualPassengerSortingEnabled
    );
  }

  getOrderedPassengerSnapshotsForCurrentMode(): IFlightBriefPassengerInfo[] {
    const selectedIds: string[] = Array.isArray(this.passengersId.value)
      ? [...this.passengersId.value]
      : [];

    return getOrderedPassengerSnapshots(
      selectedIds,
      this.getClientPassengersById(),
      this.isManualPassengerSortingEnabled
    );
  }

  addPassengerFromDropdown(): void {
    if (this.selectedPassengerToAddId) {
      if (this.selectedPassengerToAddId === this.createNewPassengerOptionValue) {
        this.openCreateClientPassengerModal();
        return;
      }

      this.togglePassenger(this.selectedPassengerToAddId);
      this.selectedPassengerToAddId = null;
    }
  }

  removeSelectedPassenger(passengerId: string): void {
    const currentIds: string[] = Array.isArray(this.passengersId.value)
      ? [...this.passengersId.value]
      : [];
    const index: number = currentIds.indexOf(passengerId);

    if (index >= 0) {
      currentIds.splice(index, 1);
      this.updateSelectedPassengerIds(currentIds);
    }
  }

  moveSelectedPassenger(passengerId: string, direction: 'up' | 'down'): void {
    if (!this.isManualPassengerSortingEnabled) {
      return;
    }

    const currentIds: string[] = Array.isArray(this.passengersId.value)
      ? [...this.passengersId.value]
      : [];
    const index: number = currentIds.indexOf(passengerId);

    if (index < 0) {
      return;
    }

    const targetIndex: number = direction === 'up' ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= currentIds.length) {
      return;
    }

    const temp = currentIds[targetIndex];
    currentIds[targetIndex] = currentIds[index];
    currentIds[index] = temp;

    this.updateSelectedPassengerIds(currentIds, false);
  }

  openEditClientPassengerModal(passenger: IClientPassenger): void {
    this.editingClientPassenger = passenger;

    this.editClientPassengerForm.reset({
      civility: passenger.civility,
      firstname: passenger.firstname,
      lastname: passenger.lastname
    });

    window.$(this.modalClientPassengerEditElement.nativeElement).modal('show');
  }

  openViewClientPassengerModal(passenger: IClientPassenger): void {
    this.viewingClientPassenger = passenger;
    window.$(this.modalClientPassengerViewElement.nativeElement).modal('show');
  }

  dismissClientPassengerViewModal(): void {
    this.viewingClientPassenger = null;
    window.$(this.modalClientPassengerViewElement.nativeElement).modal('hide');
  }

  openCreateClientPassengerModal(): void {
    this.editingClientPassenger = null;

    this.editClientPassengerForm.reset({
      civility: EnumFlightBriefPassengerCivility.mr,
      firstname: '',
      lastname: ''
    });

    window.$(this.modalClientPassengerEditElement.nativeElement).modal('show');
  }

  dismissClientPassengerEditModal(): void {
    this.savingEditedClientPassenger = false;
    this.editingClientPassenger = null;
    if (this.selectedPassengerToAddId === this.createNewPassengerOptionValue) {
      this.selectedPassengerToAddId = null;
    }
    window.$(this.modalClientPassengerEditElement.nativeElement).modal('hide');
  }

  saveEditedClientPassenger(): void {
    this.editClientPassengerForm.markAsTouched();

    if (!this.editClientPassengerForm.valid) {
      return;
    }

    this.savingEditedClientPassenger = true;

    if (!this.editingClientPassenger) {
      if (!this.enquiryFlight?.clientId) {
        this.savingEditedClientPassenger = false;
        return;
      }

      const newPassenger: IClientPassenger = {
        id: null,
        civility: this.editClientPassengerForm.value.civility,
        firstname: this.editClientPassengerForm.value.firstname,
        lastname: this.editClientPassengerForm.value.lastname,
        pipedriveOrganisationId: this.enquiryFlight.clientId
      } as IClientPassenger;

      this.clientPassengerService
        .create(newPassenger)
        .then((id: string) => {
          const createdPassenger: IClientPassenger = {
            ...newPassenger,
            id
          };
          this.clientPassengers.push(createdPassenger);
          this.togglePassenger(id);
          this.selectedPassengerToAddId = null;
          this.dismissClientPassengerEditModal();
        })
        .catch((err: any) => {
          console.log(err);
          alert(err?.message || 'Error while creating passenger');
          this.savingEditedClientPassenger = false;
        });

      return;
    }

    const updatedPassenger: IClientPassenger = {
      ...this.editingClientPassenger,
      civility: this.editClientPassengerForm.value.civility,
      firstname: this.editClientPassengerForm.value.firstname,
      lastname: this.editClientPassengerForm.value.lastname
    };

    this.clientPassengerService
      .update(updatedPassenger)
      .then(() => {
        this.clientPassengers = this.clientPassengers.map((item: IClientPassenger) =>
          item.id === updatedPassenger.id ? { ...item, ...updatedPassenger } : item
        );
        this.dismissClientPassengerEditModal();
      })
      .catch((err: any) => {
        console.log(err);
        alert(err?.message || 'Error while saving passenger');
        this.savingEditedClientPassenger = false;
      });
  }

  addPet(): void {
    this.pets.push(this.createPetFormGroup());
  }

  addPetAndSet(pet: IFlightBriefPetInfo): void {
    this.pets.push(this.createPetFormGroup(pet));
  }

  removePet(i: number): void {
    if (confirm(this.translateService.instant('GENERAL.CONFIRM_DELETE'))) {
      const sub: Subscription | undefined = this.petTypeSubscriptions[i];
      if (sub) {
        sub.unsubscribe();
        this.petTypeSubscriptions.splice(i, 1);
      }
      this.pets.removeAt(i);
    }
  }

  prev(): void {
    this.goPrev.emit();
  }

  saveAndNext(): void {
    if (this.form.valid) {
      this.saveAndGoNext.emit({
        ...this.form.value,
        passengersId: this.getOrderedPassengerIdsForCurrentMode(),
        passengers: this.getOrderedPassengerSnapshotsForCurrentMode()
      });
    }
  }

  loadClientPassengers(): void {
    this.loadingClientPassengers = true;
    this.clientPassengers = [];

    if (this.subscriptionsForClientPassengers) {
      this.subscriptionsForClientPassengers.unsubscribe();
    }

    this.subscriptionsForClientPassengers = new Subscription();

    if (!this.enquiryFlight?.clientId) {
      this.loadingClientPassengers = false;
      return;
    }

    this.subscriptionsForClientPassengers.add(
      this.clientPassengerService
        .getAllForPipedriveOrganisation(this.enquiryFlight.clientId)
        .subscribe((passengers: IClientPassenger[]) => {
          this.clientPassengers = passengers || [];
          this.loadingClientPassengers = false;
        })
    );
  }

  loadFlightBriefCaterings(): void {
    this.loadingFlightBriefCaterings = true;

    if (this.subscriptionsForCatering) {
      this.subscriptionsForCatering.unsubscribe();
    }

    this.subscriptionsForCatering = new Subscription();

    this.subscriptionsForCatering.add(
      this.flightBriefCateringService
        .getAll()
        .subscribe((flightBriefCaterings: IFlightBriefCatering[]) => {
          this.flightBriefCaterings = flightBriefCaterings;

          this.refreshDisplayedFlightBriefCaterings();

          this.loadingFlightBriefCaterings = false;
        })
    );
  }

  refreshDisplayedFlightBriefCaterings(): void {
    this.displayedFlightBriefCaterings = [];

    if (this.enquiryFlight) {
      for (const flightBriefCatering of this.flightBriefCaterings) {
        if (flightBriefCatering.enquiryTypes?.includes(this.enquiryFlight.enquiryType)) {
          this.displayedFlightBriefCaterings.push(flightBriefCatering);
        }
      }
    }
  }

  updatedFlightBriefCateringId(): void {
    if (this.flightBriefCateringId.value === 'custom') {
      this.catering.setValue(null);
    } else {
      this.catering.setValue(this.flightBriefCateringId.value);
    }

    this.catering.updateValueAndValidity();
  }

  updateLuggageQty(inc: number = 1): void {
    this.luggageQty.setValue(this.luggageQty.value + inc);

    this.refreshLuggageText();
  }

  updateLuggageWeight(inc: number = 1): void {
    this.luggagesWeight.setValue(this.luggagesWeight.value + inc);

    this.refreshLuggageText();
  }

  updateLuggagesLimitInHoldInKg(inc: number = 1): void {
    this.luggagesLimitInHoldInKg.setValue(this.luggagesLimitInHoldInKg.value + inc);

    this.refreshLuggageText();
  }

  updateLuggagesLimitInCabinInKg(inc: number = 1): void {
    this.luggagesLimitInCabinInKg.setValue(this.luggagesLimitInCabinInKg.value + inc);

    this.refreshLuggageText();
  }

  refreshLuggageText(): void {
    if (!this.luggageIsCustom.value) {
      if (
        [EnumEnquiryType.commercial, EnumEnquiryType.sport].includes(
          this.enquiryFlight?.enquiryType
        )
      ) {
        this.luggages.setValue(
          this.translationObj[
            EnumLanguage.fr
          ]?.FLIGHT_BRIEF.AIRPORT_INFORMATION.LUGGAGES_IN_HOLD_AND_CABIN.replace(
            '{{inHold}}',
            this.luggagesLimitInHoldInKg.value
          ).replace('{{inCabin}}', this.luggagesLimitInCabinInKg.value)
        );
      } else {
        this.luggages.setValue(
          this.luggageQty.value +
            'X' +
            this.luggagesWeight.value +
            this.translationObj[EnumLanguage.fr]?.FLIGHT_BRIEF.KG_BY_PAX
        );
      }
    }
  }

  updateLuggageIsCustom(): void {
    if (!this.luggageIsCustom.value) {
      this.refreshLuggageText();
    } else {
      this.luggages.setValue(null);
    }
  }

  loadTranslation(): void {
    if (this.subscriptionsTranslations) {
      this.subscriptionsTranslations.unsubscribe();
    }

    this.subscriptionsTranslations = new Subscription();

    this.subscriptionsTranslations.add(
      this.translateService.getTranslation(EnumLanguage.fr).subscribe((translationObj: any) => {
        this.translationObj[EnumLanguage.fr] = translationObj;

        this.refreshLuggageText();
      })
    );
  }

  hasAclAccess(id: EnumAcl): boolean {
    return this.aclService.hasAclAccess(id);
  }

  addFlightBriefCatering(): void {
    this.editingFlightBriefCatering = null;
    window.$(this.modalFlightBriefCateringElement.nativeElement).modal('show');
  }

  editFlightBriefCatering(): void {
    const cateringId = this.flightBriefCateringId.value;

    if (!cateringId) {
      return;
    }

    this.editingFlightBriefCatering =
      this.flightBriefCaterings.find(
        (flightBriefCatering: IFlightBriefCatering) => flightBriefCatering.id === cateringId
      ) ?? ({ id: cateringId } as IFlightBriefCatering);

    window.$(this.modalFlightBriefCateringElement.nativeElement).modal('show');
  }

  dismissFlightBriefCateringModal(newId: string | null = null): void {
    if (newId) {
      this.flightBriefCateringId.setValue(newId);
      this.flightBriefCateringId.updateValueAndValidity();
    }

    window.$(this.modalFlightBriefCateringElement.nativeElement).modal('hide');
    this.editingFlightBriefCatering = null;
  }
}
